<script lang="ts">
	import { Stack, Text, Textarea } from '@Shelly/shelly-design-system';
	import { flowEditorState } from '../../../stores/flow_editor';
	import { NOTE_THEMES, resolveNoteTheme } from '../nodes/noteThemes';

	export let nodeId: string;

	let noteText = '';
	let loadedNodeId = '';

	$: node = $flowEditorState.nodes.find((candidate) => candidate.id === nodeId);
	$: activeTheme = resolveNoteTheme(node?.data ?? {});
	$: if (node && loadedNodeId !== node.id) {
		loadedNodeId = node.id;
		noteText = String(node.data.noteText ?? '');
	}

	function eventValue(event: any): string {
		const detail = event?.detail;
		if (typeof detail === 'string') return detail;
		if (detail && typeof detail === 'object' && 'value' in detail) {
			return String(detail.value ?? '');
		}
		// Shelly DS Textarea forwards the native InputEvent as CustomEvent.detail.
		// Its value therefore lives on detail.target, not on the component event target.
		if (detail?.target && 'value' in detail.target) {
			return String(detail.target.value ?? '');
		}
		return String(event?.target?.value ?? noteText);
	}

	function updateText(event: any) {
		if (!node) return;
		noteText = eventValue(event).slice(0, 4000);
		flowEditorState.updateNode(node.id, {
			data: {
				...node.data,
				noteText,
				isLabelManuallyOverridden: true,
				displayLabel: 'Note',
			},
		});
	}

	function selectTheme(themeId: string) {
		if (!node) return;
		const theme = NOTE_THEMES.find((candidate) => candidate.id === themeId);
		if (!theme) return;
		flowEditorState.updateNode(node.id, {
			data: {
				...node.data,
				noteColor: theme.id,
				color: theme.accent,
			},
		});
	}
</script>

<Stack gap="m">
	<Textarea
		size="m"
		resize="auto"
		class="width-full note-textarea"
		label="Note text"
		placeholder="Add a reminder or explanation for this flow…"
		maxChar={4000}
		bind:value={noteText}
		on:input={updateText}
		id="flow-note-text"
		aria-label="Note text"
	/>

	<div class="color-field">
		<Text variant="body" size="s">Sticky note color</Text>
		<div class="color-options" role="radiogroup" aria-label="Sticky note color">
			{#each NOTE_THEMES as theme}
				<button
					type="button"
					class="color-option"
					class:selected={activeTheme.id === theme.id}
					style="--swatch-background: {theme.background}; --swatch-border: {theme.border}; --swatch-text: {theme.text};"
					role="radio"
					aria-checked={activeTheme.id === theme.id}
					aria-label="{theme.label} sticky note"
					on:click={() => selectTheme(theme.id)}
				>
					<i class="fas fa-note-sticky" aria-hidden="true"></i>
					<span>{theme.label}</span>
					{#if activeTheme.id === theme.id}
						<i class="fas fa-check selected-check" aria-hidden="true"></i>
					{/if}
				</button>
			{/each}
		</div>
	</div>

	<Text variant="secondary" size="sm">
		Sticky notes are saved in the flow layout only and are not executed by the device.
	</Text>
</Stack>

<style>
	/* Auto-resize may measure zero while its parent modal is still hidden. */
	:global(.note-textarea textarea) {
		min-height: 64px;
	}

	.color-field {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.color-options {
		display: grid;
		grid-template-columns: repeat(5, minmax(70px, 1fr));
		gap: 8px;
	}

	.color-option {
		position: relative;
		display: flex;
		min-height: 64px;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 5px;
		padding: 8px 6px;
		border: 2px solid transparent;
		border-radius: 9px;
		background: var(--swatch-background);
		color: var(--swatch-text);
		font: inherit;
		font-size: 11px;
		font-weight: 650;
		cursor: pointer;
		transition:
			transform 0.15s ease,
			box-shadow 0.15s ease,
			border-color 0.15s ease;
	}

	.color-option:hover {
		transform: translateY(-1px);
	}

	.color-option.selected {
		border-color: var(--swatch-border);
		box-shadow: 0 0 0 2px color-mix(in srgb, var(--swatch-border) 32%, transparent);
	}

	.color-option:focus-visible {
		outline: 2px solid #60a5fa;
		outline-offset: 2px;
	}

	.selected-check {
		position: absolute;
		top: 5px;
		right: 6px;
		font-size: 9px;
	}

	@media (max-width: 639px) {
		.color-options {
			grid-template-columns: repeat(3, minmax(80px, 1fr));
		}
	}
</style>
